<template>
  <div>
    <v-form>
      <v-text-field
        v-if="context !== 'create'"
        v-model="localReport.name"
        :label="$tc('name')"
        :error-messages="nameErrors"
        prepend-inner-icon="mdi-form-textbox"/>
      <v-row>
        <v-col>
          <vf-autocomplete
            v-model="reportCustomers"
            :items="availableCustomers"
            :error-messages="customerErrors"
            :label="$tc('customer', 2)"
            all/>
        </v-col>
        <v-col>
          <date-picker
            v-model="reportDate"
            :allowed-dates="allowedDates"
            :output-moment="true"
            :error-messages="dateErrors"
            :today="false"
            :label="$t('reports_ui.weekly_performance_report.select_a_report_week')"
            prepend-inner-icon="mdi-calendar"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            append-icon="mdi-percent"
            v-model="routeTarget"
            :label="$tc('reports_ui.weekly_performance_report.routes_in_spec_target')"
            :error-messages="routeTargetErrors"/>
        </v-col>
        <v-col>
          <v-text-field
            append-icon="mdi-percent"
            v-model="planTarget"
            :label="$tc('reports_ui.weekly_performance_report.linked_plans_target')"
            :error-messages="planTargetErrors"/>
        </v-col>
        <v-col>
          <v-text-field
            append-icon="mdi-percent"
            v-model="completionTarget"
            :label="$tc('reports_ui.weekly_performance_report.completed_root_causes_target')"
            :error-messages="completionTargetErrors"/>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import DatePicker from '@/components/shared/DatePicker'
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ReportFormMixin from '@/components/report/ReportFormMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'

import { get, map } from 'lodash'
import { required, requiredIf } from 'vuelidate/lib/validators'
import Moment from 'moment'

export default {
  name: 'WeeklyPerformanceReportForm',
  components: {
    DatePicker,
    VfAutocomplete
  },
  mixins: [
    CurrentUserMixin,
    ReportFormMixin,
    ValidationMixin
  ],
  validations: {
    reportCustomers: {
      required
    },
    localReport: {
      name: {
        required: requiredIf(function () {
          return this.action !== 'create'
        })
      }
    },
    routeTarget: {
      required
    },
    planTarget: {
      required
    },
    completionTarget: {
      required
    },
    reportDate: {
      required
    }
  },
  data () {
    return {
      reportType: 'WeeklyPerformanceReport',
      reportDate: null,
      reportCustomers: [],
      routeTarget: null,
      planTarget: null,
      completionTarget: null
    }
  },
  computed: {
    /**
     * Lists customers available to the current user (customers in their scope)
     */
    availableCustomers () {
      return map(this.customers, customer => {
        return {
          value: customer.id,
          text: customer.name
        }
      })
    },
    nameErrors () {
      return this.validationErrors([{
        prop: this.$v.localReport.name,
        message: this.$t('required')
      }])
    },
    routeTargetErrors () {
      return this.validationErrors([{
        prop: this.$v.routeTarget,
        message: this.$t('required')
      }])
    },
    planTargetErrors () {
      return this.validationErrors([{
        prop: this.$v.planTarget,
        message: this.$t('required')
      }])
    },
    completionTargetErrors () {
      return this.validationErrors([{
        prop: this.$v.completionTarget,
        message: this.$t('required')
      }])
    },
    customerErrors () {
      return this.validationErrors([{
        prop: this.$v.reportCustomers,
        message: this.$t('customer_required')
      }])
    },
    dateErrors () {
      return this.validationErrors([{
        prop: this.$v.reportDate,
        message: this.$t('required')
      }])
    }
  },
  methods: {
    /**
     * Check if the selected date is a Monday greater than a week ago.
     * @param dateString
     * @returns {boolean}
     */
    allowedDates (dateString) {
      const date = Moment(dateString)
      return date.day() === 1 && date.isBefore(Moment().subtract(1, 'week'))
    },
    /**
     * Populate pre-existing form parameters (called from mixin)
     */
    populateReportForm () {
      const preSelectedReportDate = get(this, 'report.parameters.weekDate')
      if (preSelectedReportDate) {
        this.reportDate = Moment(preSelectedReportDate)
      } else {
        // Default to the latest date we would allow to be selected, which is the latest Monday greater than a week ago.
        const oneWeekAgo = Moment().startOf('day').subtract(1, 'week')
        this.reportDate = oneWeekAgo.day(1)
      }
      this.preSelectCustomers()
      this.routeTarget = get(this, 'report.parameters.routeTarget', 98)
      this.planTarget = get(this, 'report.parameters.planTarget', 90)
      this.completionTarget = get(this, 'report.parameters.completionTarget', 95)
    },
    /**
     * Build report parameters for submission (called from mixin)
     */
    buildReportParameters () {
      const formatString = 'YYYY-MM-DD'
      const weekDate = this.reportDate
      const compareWeekDate = weekDate.clone().subtract(1, 'week').startOf('day')
      const monthDate = weekDate.clone().startOf('month')
      const compareMonthDate = monthDate.clone().subtract('1', 'month')
      const yearDate = weekDate.clone().startOf('year')
      return {
        weekDate: weekDate.format(formatString),
        compareWeekDate: compareWeekDate.format(formatString),
        monthDate: monthDate.format(formatString),
        compareMonthDate: compareMonthDate.format(formatString),
        yearDate: yearDate.format(formatString),
        customerIds: this.reportCustomers,
        routeTarget: this.routeTarget,
        planTarget: this.planTarget,
        completionTarget: this.completionTarget
      }
    },
    defaultReportName () {
      return this.$t('reports_ui.weekly_performance_report.name')
    }
  },
  created () {
    // Ensure customerContext is available before accessing it
    if (this.customerContext) {
      this.routeTarget = this.customerContext.getSetting('routesInSpecTarget', 98) // Default to 98
      this.planTarget = this.customerContext.getSetting('linkedPlansTarget', 90) // Default to 90
      this.completionTarget = this.customerContext.getSetting('rootCausesTarget', 95) // Default to 95
    } else {
      console.error('customerContext is undefined');
    }
  }
}
</script>

<style scoped>

</style>
